import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import crest from '@/assets/tfd-crest.png';
import wordmark from '@/assets/tfd-wordmark.jpg';
import { fontSize as fs, textColor as tc } from '@/design/tokens';

const DISCORD_URL = 'https://discord.com/invite/29gn3mXqtA';
const SITE_URL = 'https://tfd.rocks';
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const OG_ALT = 'The Flying Dutchman — World of Warships EU community';

const DiscordIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.6 5.6A17 17 0 0 0 15.3 4.3l-.2.4a13 13 0 0 1 3.7 1.9 14.6 14.6 0 0 0-13.6 0A13 13 0 0 1 8.9 4.7l-.2-.4A17 17 0 0 0 4.4 5.6 18.4 18.4 0 0 0 1.3 18a17.6 17.6 0 0 0 5.3 2.7l.4-.6a11.4 11.4 0 0 1-1.8-.9l.4-.3a12.6 12.6 0 0 0 10.8 0l.4.3c-.6.4-1.2.6-1.8.9l.4.6A17.6 17.6 0 0 0 22.7 18a18.4 18.4 0 0 0-3.1-12.4ZM8.5 15.3c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.9 2.1-1.9 2.1Zm7 0c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.9 2.1-1.9 2.1Z" />
  </svg>
);

const Check = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// ── line-icon set for the narrative sections ──────────────────────────────────
const ICONS: Record<string, React.ReactNode> = {
  solo: <><circle cx="12" cy="7" r="4" /><path d="M6 21v-1a6 6 0 0 1 12 0v1" /></>,
  mic: <><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8" /></>,
  growth: <><path d="M3 17l6-6 4 4 8-8" /><path d="M16 7h5v5" /></>,
  friendly: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  coordination: <><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="5" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="M8.2 11l7.6-4.6M8.2 13l7.6 4.6" /></>,
  event: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  trophy: <><path d="M7 4h10v5a5 5 0 0 1-10 0V4z" /><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 0-3 3M9 21h6M12 14v7" /></>,
  controller: <><rect x="2" y="6" width="20" height="12" rx="5" /><path d="M7 11v3M5.5 12.5h3" /><circle cx="16" cy="11" r="1" /><circle cx="18.5" cy="14" r="1" /></>,
  chart: <><path d="M4 20h16" /><path d="M7 20v-4" /><path d="M12 20v-8" /><path d="M17 20v-12" /></>,
  sliders: <><path d="M4 6h16M4 12h16M4 18h16" /><circle cx="9" cy="6" r="2" /><circle cx="15" cy="12" r="2" /><circle cx="7" cy="18" r="2" /></>,
};
const Icon = ({ name, size = 24 }: { name: keyof typeof ICONS; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name]}
  </svg>
);

const goldButton: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '10px', borderRadius: '5px',
  background: 'linear-gradient(180deg,#ecd28b,#c49a3f)', color: '#1a1206',
  fontFamily: "'Barlow',sans-serif", fontWeight: 700, letterSpacing: '.12em',
  textTransform: 'uppercase', textDecoration: 'none', border: '1px solid #8a6d2c',
};

const sectionLabel: React.CSSProperties = {
  fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.38em',
  color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: '16px',
};

const sectionHeading: React.CSSProperties = {
  fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(30px,4.6vw,52px)',
  lineHeight: 1.08, margin: 0, color: tc.strong,
};

const Alt = () => {
  useEffect(() => {
    // TFD crest fly-to-nav scroll effect — reads the in-flow anchor's live
    // viewport rect each frame and moves the fixed crest along an arc
    // (rises north first, then curves west into the nav slot).
    let startTop: number | null = null;
    let navSolid: boolean | null = null;
    let raf = 0;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const update = () => {
      const fly = document.getElementById('tfd-fly');
      const anchor = document.getElementById('tfd-anchor');
      if (!fly || !anchor) return;
      const rect = anchor.getBoundingClientRect();
      if (!rect.width) return;
      if (startTop === null) startTop = rect.top;

      const vh = window.innerHeight;
      const deadZone = vh * 0.015;
      const travel = vh * 0.72;
      const scrolled = Math.max(0, startTop - rect.top);
      const t = Math.min(1, Math.max(0, (scrolled - deadZone) / travel));
      const u = easeInOutCubic(t);

      const endSize = 44;
      const padLeft = Math.max(18, Math.min(52, window.innerWidth * 0.05));
      const navCx = padLeft + endSize / 2;
      const navCy = 33;

      const sx = rect.left + rect.width / 2;
      const sy = rect.top + rect.width / 2;
      const cpx = sx;
      const cpy = navCy;
      const omu = 1 - u;
      const cx = omu * omu * sx + 2 * omu * u * cpx + u * u * navCx;
      const cy = omu * omu * sy + 2 * omu * u * cpy + u * u * navCy;

      const floatY = Math.sin(performance.now() / 1034) * 16 * (1 - u);
      const size = rect.width + (endSize - rect.width) * u;
      fly.style.width = size + 'px';
      fly.style.left = cx - size / 2 + 'px';
      fly.style.top = cy + floatY - size / 2 + 'px';

      const img = fly.firstElementChild as HTMLElement | null;
      if (img) {
        img.style.animation = 'none';
        const g = 1 - u;
        img.style.filter =
          'drop-shadow(0 ' + (28 * g + 3).toFixed(1) + 'px ' + (60 * g + 6).toFixed(1) +
          'px rgba(0,0,0,.6)) drop-shadow(0 0 ' + (40 * g).toFixed(1) +
          'px rgba(58,150,140,' + (0.25 * g).toFixed(3) + '))';
      }

      const nav = document.getElementById('tfd-nav');
      if (nav) {
        const solid = Math.sign(scrolled - 24) === 1;
        if (solid !== navSolid) {
          navSolid = solid;
          nav.style.background = solid ? 'linear-gradient(180deg,rgba(6,11,14,.95),rgba(6,11,14,.85))' : 'transparent';
          nav.style.boxShadow = solid ? '0 8px 30px rgba(0,0,0,.5)' : 'none';
          nav.style.borderBottomColor = solid ? 'rgba(201,162,75,.2)' : 'rgba(201,162,75,0)';
        }
      }
    };

    const loop = () => {
      update();
      raf = requestAnimationFrame(loop);
    };

    const onResize = () => { startTop = null; update(); };

    raf = requestAnimationFrame(loop);
    window.addEventListener('scroll', update, { passive: true, capture: true });
    document.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', onResize);
    update();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, { capture: true } as EventListenerOptions);
      document.removeEventListener('scroll', update, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const pageTitle = 'The Flying Dutchman — Play World of Warships with a Crew (EU)';
  const pageDesc =
    'A friendly World of Warships crew on the EU server since 2017 — real comms, proper teamwork, ' +
    'and a clan for every pace. Come play with people who talk back.';

  const needs = [
    {
      tone: 'teal' as const, icon: 'solo' as const,
      title: 'Grinding solo gets old',
      body: "The game's still good, but battle after battle with randoms who scatter the second it starts takes away from that. Run a division with your mates and watch the victories roll in.",
    },
    {
      tone: 'gold' as const, icon: 'mic' as const,
      title: "You're calling pushes nobody hears",
      body: "You spot the flank, you call the push — but it falls on deaf ears. The plan dies in your head.",
    },
    {
      tone: 'gold' as const, icon: 'growth' as const,
      title: 'Guides alone only take you so far',
      body: "The game clicks when someone talks you through it mid-match — match after match beats solo trial and error. We've got plenty of experience to share.",
    },
    {
      tone: 'teal' as const, icon: 'coordination' as const,
      title: 'Skillfully organised Clan Battles',
      body: "Seven captains, one tactic, everyone working tight together. Like chess, every move counts — Clan Battles is where your skill and strategy are put to the test.",
    },
  ];

  const pillars = [
    {
      tone: 'gold' as const, icon: 'growth' as const,
      title: 'Get better by playing together',
      body: "Division up with captains who share what they're seeing and why, talk through what happened, get sharper as you go. No homework, just play.",
    },
    {
      tone: 'teal' as const, icon: 'friendly' as const,
      title: 'Friendly first, always',
      body: "No toxicity, no gatekeeping. You don't have to be a unicum — bring a good attitude and a willingness to learn, and you'll fit right in. International crew, English is our shared tongue, all flags welcome.",
    },
    {
      tone: 'gold' as const, icon: 'chart' as const,
      title: 'Feedback backed by data',
      body: "We actually look at the numbers — your stats and replays — and give you honest, specific pointers, especially on your Clan Battles play. You'll know what to work on next.",
    },
    {
      tone: 'teal' as const, icon: 'sliders' as const,
      title: "A community that's actually run",
      body: "We put real work into keeping things organised — modern tools, easy ways to find a division or a group for the night, and a crew that keeps it active. Not a Discord you mute and forget.",
    },
  ];

  const extras = [
    {
      icon: 'event' as const,
      title: 'Clan events of our own',
      body: 'Crew nights inside the clan — themed games, friendly challenges, the odd bit of organised chaos.',
    },
    {
      icon: 'trophy' as const,
      title: 'Tournaments together',
      body: 'We field teams for tournaments through the season. Bring your best games, or just come watch from voice and cheer the crew on — both count.',
    },
    {
      icon: 'controller' as const,
      title: 'Game nights off the water',
      body: "We don't only play Warships. When the fleet's docked you'll find us in other games together — the crew's the point, the ships are just where we met.",
    },
  ];

  const checklist = [
    "Every skill level welcome — bring the attitude, we'll help with the rest",
    'English-speaking and international, all on the EU server since 2017',
    'Three clans, from serious Clan Battles to relaxed and social',
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <meta name="theme-color" content="#05090c" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The Flying Dutchman" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:secure_url" content={OG_IMAGE} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={OG_ALT} />
        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={OG_ALT} />

        <style>{`
          /* 3-card rows resolve to 3-up or 1-up only — never a lonely 2+1 orphan. */
          .alt-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
          @media (max-width: 920px) { .alt-grid-3 { grid-template-columns: minmax(0, 1fr); } }
        `}</style>
      </Helmet>

      <div data-screen-label="TFD Home" style={{
        position: 'relative',
        background:
          'radial-gradient(1100px 760px at 50% -8%, rgba(46,125,116,.18), transparent 55%),' +
          'radial-gradient(820px 600px at 82% 22%, rgba(46,125,116,.08), transparent 55%),' +
          'linear-gradient(180deg,#070d10 0%,#05090c 55%,#04080b 100%)',
      }}>

        {/* atmosphere: fog, vignette, grain */}
        <div aria-hidden="true" style={{ position: 'fixed', inset: '-10% -10% auto -10%', height: '78vh', pointerEvents: 'none', zIndex: 0, opacity: .5, background: 'radial-gradient(60% 80% at 28% 18%, rgba(58,150,140,.20), transparent 60%)', filter: 'blur(46px)', animation: 'fogdrift 28s ease-in-out infinite alternate' }} />
        <div aria-hidden="true" style={{ position: 'fixed', inset: '-10% -10% auto -10%', height: '78vh', pointerEvents: 'none', zIndex: 0, opacity: .4, background: 'radial-gradient(50% 70% at 72% 8%, rgba(46,125,116,.22), transparent 62%)', filter: 'blur(54px)', animation: 'fogdrift 38s ease-in-out infinite alternate-reverse' }} />
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2, background: 'radial-gradient(130% 100% at 50% 32%, transparent 48%, rgba(0,0,0,.55) 100%)' }} />
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2, opacity: .4, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.012) 0px, rgba(255,255,255,.012) 1px, transparent 1px, transparent 3px), repeating-linear-gradient(90deg, rgba(0,0,0,.05) 0px, rgba(0,0,0,.05) 1px, transparent 1px, transparent 3px)' }} />

        {/* crest that flies from hero centre up into the nav on scroll */}
        <a id="tfd-fly" href="#top" aria-label="The Flying Dutchman — back to top" style={{ position: 'fixed', zIndex: 60, display: 'block', left: 'calc(50% - 150px)', top: '120px', width: '300px', lineHeight: 0, textDecoration: 'none' }}>
          <img src={crest} alt="TFD crest" style={{ width: '100%', height: 'auto', display: 'block', filter: 'drop-shadow(0 28px 60px rgba(0,0,0,.7)) drop-shadow(0 0 40px rgba(58,150,140,.25))' }} />
        </a>

        {/* NAV — crest lands here on scroll; content right-aligned */}
        <nav id="tfd-nav" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', padding: '12px clamp(18px,5vw,52px)', background: 'transparent', borderBottom: '1px solid rgba(201,162,75,0)', transition: 'background .35s ease, box-shadow .35s ease, border-color .35s ease' }}>
          <Link to="/cb" className="tfd-link" style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.18em', textTransform: 'uppercase', color: tc.default, textDecoration: 'none', fontWeight: 600 }}>Clan Battles</Link>
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-btn-gold" style={{ ...goldButton, padding: '10px 20px', borderRadius: '4px', fontSize: fs.caption, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.45), 0 6px 18px rgba(201,162,75,.22)' }}>
            <DiscordIcon size={16} />
            Discord
          </a>
        </nav>

        {/* HERO — crest on top, then the hook */}
        <header id="top" style={{ position: 'relative', zIndex: 3, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '118px clamp(20px,5vw,40px) 84px' }}>
          <div aria-hidden="true" style={{ position: 'absolute', top: '12%', left: '50%', transform: 'translateX(-50%)', width: 'min(640px,94vw)', height: 'min(640px,94vw)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(58,150,140,.30), rgba(46,125,116,.06) 52%, transparent 70%)', filter: 'blur(12px)', animation: 'pulseGlow 7.5s ease-in-out infinite', zIndex: 0 }} />

          <img id="tfd-anchor" src={crest} alt="" aria-hidden="true" style={{ position: 'relative', zIndex: 1, width: 'min(300px,64vw)', height: 'auto', opacity: 0, marginBottom: '30px' }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', animation: 'fadeUp .9s ease-out both' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px', fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.32em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: '26px' }}>
              <span style={{ width: '28px', height: '1px', background: 'linear-gradient(90deg,transparent,#c9a24b)' }} />
              The Flying Dutchman · EU Server · Since 2017
              <span style={{ width: '28px', height: '1px', background: 'linear-gradient(90deg,#c9a24b,transparent)' }} />
            </div>

            <h1 style={{ fontFamily: "'Cinzel',serif", fontWeight: 400, fontSize: 'clamp(38px,7.4vw,86px)', lineHeight: 1.04, margin: '0 0 8px', letterSpacing: '.02em', color: tc.strong, filter: 'drop-shadow(0 3px 10px rgba(0,0,0,.5))' }}>
              Warships is better<br />with a crew<br /><span className="goldtext">that wins together</span>
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', margin: '26px 0 26px' }}>
              <span style={{ height: '1px', width: '72px', background: 'linear-gradient(90deg,transparent,#8a6d2c)' }} />
              <span style={{ width: '7px', height: '7px', transform: 'rotate(45deg)', background: '#c9a24b', boxShadow: '0 0 12px rgba(201,162,75,.7)' }} />
              <span style={{ height: '1px', width: '72px', background: 'linear-gradient(90deg,#8a6d2c,transparent)' }} />
            </div>

            <p style={{ fontSize: fs.lead, lineHeight: 1.65, color: tc.default, maxWidth: '620px', margin: '0 auto 36px' }}>
              You like this game. You'd just rather not grind it alone next to silent strangers. We're a friendly, international community on the EU server — people who talk to you, support each other, and enjoy some banter.
            </p>

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-btn-gold" style={{ ...goldButton, padding: '16px 32px', fontSize: fs.body14, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.5), 0 12px 34px rgba(201,162,75,.28)' }}>
                <DiscordIcon size={17} />
                Join the Discord
              </a>
              <a href="#fleet" className="tfd-btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 32px', borderRadius: '5px', background: 'rgba(58,150,140,.06)', color: tc.default, fontFamily: "'Barlow',sans-serif", fontWeight: 600, fontSize: fs.body14, letterSpacing: '.12em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid rgba(79,179,165,.4)' }}>
                See who we are
              </a>
            </div>
          </div>

          <div aria-hidden="true" style={{ position: 'absolute', bottom: '22px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: tc.muted, animation: 'bob 2.8s ease-in-out infinite' }}>
            <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.3em', textTransform: 'uppercase' }}>Scroll</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
          </div>
        </header>

        {/* THE FOUR NEEDS — recognition */}
        <section style={{ position: 'relative', zIndex: 3, padding: 'clamp(70px,9vw,120px) clamp(20px,5vw,40px)', borderTop: '1px solid rgba(201,162,75,.1)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '52px' }}>
              <div style={sectionLabel}>Sound familiar?</div>
              <h2 style={sectionHeading}>Four things solo queue can't give you</h2>
              <p style={{ fontSize: fs.body, lineHeight: 1.7, color: tc.muted, maxWidth: '600px', margin: '20px auto 0' }}>
                You still love Warships. It's the lack of teamplay that wears thin — no callouts, no plan, each teammate doing their own thing. Here's what the TFD community can give you:
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: '18px', maxWidth: '880px', margin: '0 auto' }}>
              {needs.map((n) => {
                const accentRgb = n.tone === 'gold' ? '201,162,75' : '79,179,165';
                const iconColor = n.tone === 'gold' ? '#ecd28b' : '#6fd3c4';
                return (
                  <div key={n.title} style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', padding: '28px 26px', borderRadius: '6px', background: 'linear-gradient(180deg,rgba(14,24,26,.7),rgba(8,14,16,.72))', border: `1px solid rgba(${accentRgb},.16)` }}>
                    <span style={{ flexShrink: 0, width: '46px', height: '46px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `rgba(${accentRgb},.1)`, border: `1px solid rgba(${accentRgb},.25)`, color: iconColor }}>
                      <Icon name={n.icon} size={22} />
                    </span>
                    <div>
                      <h3 style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: fs.h6, margin: '0 0 9px', color: tc.strong, letterSpacing: '.02em', lineHeight: 1.2 }}>{n.title}</h3>
                      <p style={{ fontSize: fs.body, lineHeight: 1.6, color: tc.muted, margin: 0 }}>{n.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* BRIDGE — the pivot */}
        <section style={{ position: 'relative', zIndex: 3, padding: '0 clamp(20px,5vw,40px) clamp(20px,4vw,40px)' }}>
          <div style={{ position: 'relative', maxWidth: '760px', margin: '0 auto', textAlign: 'center', padding: 'clamp(36px,5vw,56px) clamp(28px,5vw,52px)', borderRadius: '10px', background: 'linear-gradient(180deg,rgba(16,28,30,.7),rgba(8,14,16,.7))', border: '1px solid rgba(79,179,165,.22)', boxShadow: 'inset 0 0 0 1px rgba(79,179,165,.04), 0 24px 54px rgba(0,0,0,.4)' }}>
            <span aria-hidden="true" style={{ position: 'absolute', top: '14px', left: '14px', width: '16px', height: '16px', borderTop: '1.5px solid #4fb3a5', borderLeft: '1.5px solid #4fb3a5', opacity: .6 }} />
            <span aria-hidden="true" style={{ position: 'absolute', bottom: '14px', right: '14px', width: '16px', height: '16px', borderBottom: '1.5px solid #4fb3a5', borderRight: '1.5px solid #4fb3a5', opacity: .6 }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '20px' }}>
              <span style={{ height: '1px', width: '46px', background: 'linear-gradient(90deg,transparent,#4fb3a5)' }} />
              <span style={{ width: '7px', height: '7px', transform: 'rotate(45deg)', background: '#4fb3a5', boxShadow: '0 0 12px rgba(79,179,165,.7)' }} />
              <span style={{ height: '1px', width: '46px', background: 'linear-gradient(90deg,#4fb3a5,transparent)' }} />
            </div>
            <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(24px,3.6vw,40px)', lineHeight: 1.14, margin: '0 0 18px', color: tc.strong }}>
              So we built the crew we wanted to play with
            </h2>
            <p style={{ fontSize: fs.lead, lineHeight: 1.7, color: tc.muted, maxWidth: '600px', margin: '0 auto' }}>
              The Flying Dutchman has been doing this since 2017: friendly captains from across Europe on the EU server, playing together every night. Three clans under one flag — take your pick below.
            </p>
          </div>
        </section>

        {/* SOCIAL PROOF STRIP */}
        <section style={{ position: 'relative', zIndex: 3, padding: 'clamp(28px,4vw,44px) clamp(20px,5vw,40px)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '1px', background: 'rgba(201,162,75,.18)', border: '1px solid rgba(201,162,75,.22)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.4)' }}>
            {[
              { value: '80+', label: 'Captains', gold: true },
              { value: '3', label: 'Clans, One Flag', gold: false },
              { value: '2017', label: 'Sailing Since', gold: false },
              { value: 'EU', label: 'Home Server', gold: true },
            ].map((s) => (
              <div key={s.label} style={{ background: 'linear-gradient(180deg,#0c171a,#091215)', padding: '32px 20px', textAlign: 'center' }}>
                <div className={s.gold ? 'goldtext' : undefined} style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 'clamp(32px,5vw,46px)', lineHeight: 1, color: s.gold ? undefined : '#6fd3c4' }}>{s.value}</div>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.2em', textTransform: 'uppercase', color: tc.muted, marginTop: '9px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS — what it feels like aboard */}
        <section style={{ position: 'relative', zIndex: 3, padding: 'clamp(70px,9vw,120px) clamp(20px,5vw,40px)' }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '58px' }}>
              <div style={sectionLabel}>Once you're aboard</div>
              <h2 style={sectionHeading}>What you get with us</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(248px,1fr))', gap: '20px' }}>
              {pillars.map((c) => {
                const accent = c.tone === 'gold' ? '#c9a24b' : '#4fb3a5';
                const iconColor = c.tone === 'gold' ? '#ecd28b' : '#6fd3c4';
                const accentRgb = c.tone === 'gold' ? '201,162,75' : '79,179,165';
                return (
                  <div key={c.title} className="tfd-lift" style={{ position: 'relative', padding: '34px 28px', borderRadius: '6px', background: 'linear-gradient(180deg,rgba(16,28,30,.92),rgba(8,14,16,.94))', border: `1px solid rgba(${accentRgb},.22)`, boxShadow: `inset 0 0 0 1px rgba(${accentRgb},.05), 0 24px 50px rgba(0,0,0,.4)`, ['--hovbc' as string]: `rgba(${accentRgb},.5)` } as React.CSSProperties}>
                    <span aria-hidden="true" style={{ position: 'absolute', top: '10px', left: '10px', width: '14px', height: '14px', borderTop: `1.5px solid ${accent}`, borderLeft: `1.5px solid ${accent}`, opacity: .6 }} />
                    <span aria-hidden="true" style={{ position: 'absolute', bottom: '10px', right: '10px', width: '14px', height: '14px', borderBottom: `1.5px solid ${accent}`, borderRight: `1.5px solid ${accent}`, opacity: .6 }} />
                    <div style={{ width: '50px', height: '50px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `rgba(${accentRgb},.1)`, border: `1px solid rgba(${accentRgb},.25)`, color: iconColor, marginBottom: '20px' }}>
                      <Icon name={c.icon} size={24} />
                    </div>
                    <h3 style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: fs.h6, margin: '0 0 10px', color: tc.strong, letterSpacing: '.02em' }}>{c.title}</h3>
                    <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, margin: 0 }}>{c.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* THREE CLANS — find your pace */}
        <section id="fleet" style={{ position: 'relative', zIndex: 3, padding: 'clamp(40px,6vw,80px) clamp(20px,5vw,40px) clamp(70px,9vw,120px)' }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '58px' }}>
              <div style={sectionLabel}>Find your berth</div>
              <h2 style={{ ...sectionHeading, margin: '0 0 16px' }}>A clan for every pace</h2>
              <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, maxWidth: '560px', margin: '0 auto' }}>Chasing the top or just here to unwind, one of our three clans fits you.</p>
            </div>
            <div className="alt-grid-3" style={{ gap: '24px' }}>

              {/* TFD */}
              <div className="tfd-clan" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: 'linear-gradient(180deg,rgba(18,30,32,.95),rgba(7,12,14,.96))', border: '1px solid rgba(201,162,75,.32)', padding: '36px 30px', boxShadow: 'inset 0 0 0 1px rgba(201,162,75,.06), 0 24px 54px rgba(0,0,0,.45)' }}>
                <div aria-hidden="true" style={{ position: 'absolute', top: '-50px', right: '-50px', width: '190px', height: '190px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(201,162,75,.2),transparent 70%)' }} />
                <span aria-hidden="true" style={{ position: 'absolute', top: '12px', left: '12px', width: '16px', height: '16px', borderTop: '1.5px solid #c9a24b', borderLeft: '1.5px solid #c9a24b', opacity: .7 }} />
                <span aria-hidden="true" style={{ position: 'absolute', bottom: '12px', right: '12px', width: '16px', height: '16px', borderBottom: '1.5px solid #c9a24b', borderRight: '1.5px solid #c9a24b', opacity: .7 }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                  <span className="goldtext" style={{ fontFamily: "'Cinzel',serif", fontWeight: 900, fontSize: '54px', lineHeight: .9, letterSpacing: '.03em' }}>TFD</span>
                  <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: tc.gold, background: 'rgba(201,162,75,.12)', border: '1px solid rgba(201,162,75,.3)', padding: '6px 11px', borderRadius: '3px' }}>Flagship</span>
                </div>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.16em', textTransform: 'uppercase', color: tc.muted, marginBottom: '14px' }}>Competitive · Main Clan</div>
                <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, margin: '0 0 22px' }}>Our flagship for seasoned captains chasing the top. Serious Clan Battles, coordinated teams, always climbing.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['Clan Battles', 'Top Tier'].map((tag) => (
                    <span key={tag} style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.04em', color: tc.default, background: 'rgba(79,179,165,.08)', border: '1px solid rgba(79,179,165,.2)', padding: '5px 12px', borderRadius: '3px' }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* TFX */}
              <div className="tfd-clan" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: 'linear-gradient(180deg,rgba(18,30,32,.95),rgba(7,12,14,.96))', border: '1px solid rgba(79,179,165,.34)', padding: '36px 30px', boxShadow: 'inset 0 0 0 1px rgba(79,179,165,.06), 0 24px 54px rgba(0,0,0,.45)' }}>
                <div aria-hidden="true" style={{ position: 'absolute', top: '-50px', right: '-50px', width: '190px', height: '190px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(79,179,165,.22),transparent 70%)' }} />
                <span aria-hidden="true" style={{ position: 'absolute', top: '12px', left: '12px', width: '16px', height: '16px', borderTop: '1.5px solid #4fb3a5', borderLeft: '1.5px solid #4fb3a5', opacity: .7 }} />
                <span aria-hidden="true" style={{ position: 'absolute', bottom: '12px', right: '12px', width: '16px', height: '16px', borderBottom: '1.5px solid #4fb3a5', borderRight: '1.5px solid #4fb3a5', opacity: .7 }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                  <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 900, fontSize: '54px', lineHeight: .9, letterSpacing: '.03em', background: 'linear-gradient(180deg,#aef0e4,#4fb3a5 55%,#2e7d74)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>TFX</span>
                  <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: tc.teal, background: 'rgba(79,179,165,.14)', border: '1px solid rgba(79,179,165,.32)', padding: '6px 11px', borderRadius: '3px' }}>Rising</span>
                </div>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.16em', textTransform: 'uppercase', color: tc.muted, marginBottom: '14px' }}>Divisions · Community</div>
                <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, margin: '0 0 22px' }}>The beating heart of the crew — captains on the rise. Nightly divisions, learning the ropes, and getting sharper every season together.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['Divisions', 'On the climb'].map((tag) => (
                    <span key={tag} style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.04em', color: tc.default, background: 'rgba(79,179,165,.08)', border: '1px solid rgba(79,179,165,.2)', padding: '5px 12px', borderRadius: '3px' }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* TFO */}
              <div className="tfd-clan" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: 'linear-gradient(180deg,rgba(18,30,32,.95),rgba(7,12,14,.96))', border: '1px solid rgba(160,176,170,.26)', padding: '36px 30px', boxShadow: 'inset 0 0 0 1px rgba(160,176,170,.05), 0 24px 54px rgba(0,0,0,.45)' }}>
                <div aria-hidden="true" style={{ position: 'absolute', top: '-50px', right: '-50px', width: '190px', height: '190px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(160,176,170,.14),transparent 70%)' }} />
                <span aria-hidden="true" style={{ position: 'absolute', top: '12px', left: '12px', width: '16px', height: '16px', borderTop: '1.5px solid #a7b3ad', borderLeft: '1.5px solid #a7b3ad', opacity: .6 }} />
                <span aria-hidden="true" style={{ position: 'absolute', bottom: '12px', right: '12px', width: '16px', height: '16px', borderBottom: '1.5px solid #a7b3ad', borderRight: '1.5px solid #a7b3ad', opacity: .6 }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                  <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 900, fontSize: '54px', lineHeight: .9, letterSpacing: '.03em', background: 'linear-gradient(180deg,#eef2f0,#bac4be 55%,#8a958f)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>TFO</span>
                  <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: tc.default, background: 'rgba(160,176,170,.12)', border: '1px solid rgba(160,176,170,.28)', padding: '6px 11px', borderRadius: '3px' }}>Veterans</span>
                </div>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.16em', textTransform: 'uppercase', color: tc.muted, marginBottom: '14px' }}>Relaxed · Old Guard</div>
                <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, margin: '0 0 22px' }}>The relaxed clan for veterans who still enjoy the game. No schedules, no quotas — just good company and the occasional battle.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['Casual', 'Social'].map((tag) => (
                    <span key={tag} style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.04em', color: tc.default, background: 'rgba(79,179,165,.08)', border: '1px solid rgba(79,179,165,.2)', padding: '5px 12px', borderRadius: '3px' }}>{tag}</span>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CINEMATIC SHIP BAND */}
        <section style={{ position: 'relative', zIndex: 3, minHeight: 'clamp(340px,44vw,500px)', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden', borderBlock: '1px solid rgba(201,162,75,.12)' }}>
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${wordmark})`, backgroundSize: 'cover', backgroundPosition: 'center 38%', opacity: .5 }} />
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(5,9,12,.92),rgba(5,9,12,.55) 50%,rgba(5,9,12,.92)), linear-gradient(180deg,rgba(5,9,12,.95),rgba(5,9,12,.4) 40%,rgba(5,9,12,.95))' }} />
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(60% 80% at 50% 50%, rgba(58,150,140,.16), transparent 65%)' }} />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '720px', padding: '60px clamp(24px,5vw,40px)' }}>
            <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.4em', color: tc.teal, textTransform: 'uppercase', fontWeight: 600, marginBottom: '18px' }}>EU Clan Battles · Since 2017</div>
            <h2 className="goldtext" style={{ fontFamily: "'Cinzel',serif", fontWeight: 400, fontSize: 'clamp(28px,5vw,56px)', lineHeight: 1.08, margin: '0 0 18px', letterSpacing: '.02em', filter: 'drop-shadow(0 3px 12px rgba(0,0,0,.6))' }}>Nine years, charted</h2>
            <p style={{ fontSize: fs.lead, lineHeight: 1.6, color: tc.default, maxWidth: '540px', margin: '0 auto 28px' }}>Every EU Clan Battles season since 2017 — battles, captains and tiers, mapped into one interactive chart.</p>
            <Link to="/cb" className="tfd-btn-gold" style={{ ...goldButton, padding: '15px 30px', fontSize: fs.body14, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.5), 0 12px 30px rgba(201,162,75,.26)' }}>
              Explore the history
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Link>
          </div>
        </section>

        {/* BEYOND THE BATTLE — extras */}
        <section style={{ position: 'relative', zIndex: 3, padding: 'clamp(70px,9vw,120px) clamp(20px,5vw,40px)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '52px' }}>
              <div style={sectionLabel}>Beyond the battle</div>
              <h2 style={sectionHeading}>More than the grind</h2>
            </div>
            <div className="alt-grid-3" style={{ gap: '18px' }}>
              {extras.map((e, i) => {
                const accentRgb = i === 1 ? '201,162,75' : '79,179,165';
                const iconColor = i === 1 ? '#ecd28b' : '#6fd3c4';
                return (
                  <div key={e.title} style={{ padding: '32px 28px', borderRadius: '6px', background: 'linear-gradient(180deg,rgba(14,24,26,.7),rgba(8,14,16,.72))', border: `1px solid rgba(${accentRgb},.16)` }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `rgba(${accentRgb},.1)`, border: `1px solid rgba(${accentRgb},.25)`, color: iconColor, marginBottom: '18px' }}>
                      <Icon name={e.icon} size={23} />
                    </div>
                    <h3 style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: fs.h6, margin: '0 0 10px', color: tc.strong, letterSpacing: '.02em' }}>{e.title}</h3>
                    <p style={{ fontSize: fs.body, lineHeight: 1.65, color: tc.muted, margin: 0 }}>{e.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ENLIST — one clear ask */}
        <section id="join" style={{ position: 'relative', zIndex: 3, padding: 'clamp(70px,9vw,120px) clamp(20px,5vw,40px)', borderTop: '1px solid rgba(201,162,75,.1)' }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: '48px', alignItems: 'start' }}>

            <div>
              <div style={sectionLabel}>Hoist the colours</div>
              <h2 style={{ ...sectionHeading, margin: '0 0 22px' }}>Weigh anchor with{'\u00A0'}us</h2>
              <p style={{ fontSize: fs.body, lineHeight: 1.7, color: tc.muted, margin: '0 0 30px', maxWidth: '480px' }}>Our Discord is where it all happens. Drop in, say hello, and play a few games as a guest first. Like it? An officer will help you find a clan and get you set up. No pressure, no audition.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '32px' }}>
                {checklist.map((line) => (
                  <div key={line} style={{ display: 'flex', alignItems: 'center', gap: '13px', color: tc.default, fontSize: fs.body }}>
                    <span style={{ color: tc.teal, flexShrink: 0 }}><Check /></span>{line}
                  </div>
                ))}
              </div>

              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 26px', borderRadius: '5px', background: 'rgba(58,150,140,.07)', color: tc.default, fontFamily: "'Barlow',sans-serif", fontWeight: 600, fontSize: fs.small, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid rgba(79,179,165,.38)' }}>
                <DiscordIcon />
                Just join the Discord
              </a>
            </div>

            {/* discord card */}
            <div style={{ position: 'relative', borderRadius: '10px', background: 'linear-gradient(180deg,rgba(18,30,32,.96),rgba(7,12,14,.97))', border: '1px solid rgba(201,162,75,.3)', padding: 'clamp(34px,4vw,52px)', boxShadow: 'inset 0 0 0 1px rgba(201,162,75,.06), 0 34px 80px rgba(0,0,0,.5)', textAlign: 'center', overflow: 'hidden' }}>
              {[
                { top: '14px', left: '14px', bt: true, bl: true },
                { top: '14px', right: '14px', bt: true, br: true },
                { bottom: '14px', left: '14px', bb: true, bl: true },
                { bottom: '14px', right: '14px', bb: true, br: true },
              ].map((p, i) => (
                <span key={i} aria-hidden="true" style={{ position: 'absolute', top: p.top, left: p.left, right: p.right, bottom: p.bottom, width: '18px', height: '18px', borderTop: p.bt ? '1.5px solid #c9a24b' : undefined, borderBottom: p.bb ? '1.5px solid #c9a24b' : undefined, borderLeft: p.bl ? '1.5px solid #c9a24b' : undefined, borderRight: p.br ? '1.5px solid #c9a24b' : undefined, opacity: .7 }} />
              ))}
              <div aria-hidden="true" style={{ position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '240px', height: '240px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(58,150,140,.22),transparent 70%)' }} />
              <img src={crest} alt="" style={{ position: 'relative', width: '104px', height: 'auto', margin: '0 auto 22px', filter: 'drop-shadow(0 0 26px rgba(58,150,140,.4))' }} />
              <h3 style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: 'clamp(22px,3vw,28px)', margin: '0 0 14px', color: tc.strong, letterSpacing: '.03em' }}>Come play a few games</h3>
              <p style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.body, lineHeight: 1.65, color: tc.muted, maxWidth: '340px', margin: '0 auto 28px' }}>Join us in voice tonight — as a guest, or apply for one of our clans. Either way, you're welcome on deck.</p>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-btn-gold" style={{ ...goldButton, padding: '16px 34px', borderRadius: '6px', fontSize: fs.body15, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.5), 0 12px 30px rgba(201,162,75,.26)' }}>
                <DiscordIcon size={19} />
                Join the Discord
              </a>
            </div>

          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ position: 'relative', zIndex: 3, borderTop: '1px solid rgba(201,162,75,.12)', padding: '48px clamp(20px,5vw,40px) 40px' }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <img src={crest} alt="TFD" style={{ height: '50px', width: 'auto' }} />
              <div>
                <div className="goldtext" style={{ fontFamily: "'Cinzel',serif", fontWeight: 800, fontSize: '17px', letterSpacing: '.14em' }}>THE FLYING DUTCHMAN</div>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.08em', color: tc.muted, marginTop: '3px' }}>World of Warships · EU Server · Since 2017</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: tc.muted, textDecoration: 'none', fontFamily: "'Barlow',sans-serif", fontSize: fs.small, letterSpacing: '.06em' }}>
                <DiscordIcon size={19} />
                Discord
              </a>
              <Link to="/cb" className="tfd-link" style={{ color: tc.muted, textDecoration: 'none', fontFamily: "'Barlow',sans-serif", fontSize: fs.small, letterSpacing: '.06em' }}>Clan Battles</Link>
              <a href="#top" className="tfd-link" style={{ color: tc.muted, textDecoration: 'none', fontFamily: "'Barlow',sans-serif", fontSize: fs.small, letterSpacing: '.06em' }}>Back to top</a>
            </div>
          </div>
          <div style={{ maxWidth: '1180px', margin: '28px auto 0', paddingTop: '24px', borderTop: '1px solid rgba(201,162,75,.07)', fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted }}>© 2026 The Flying Dutchman. Not affiliated with Wargaming. World of Warships is a trademark of Wargaming.net.</div>
        </footer>

      </div>
    </>
  );
};

export default Alt;
